/**
 * Renders a DOM element to an image via an SVG <foreignObject>. Used for text-based items
 * (notes, titles, rich blocks, kanban, link cards, file chips) whose look comes from CSS.
 * Computed styles are inlined because the SVG renders in its own document, and Google Fonts
 * in use are embedded as data-URI @font-face rules for the same reason.
 */

const STYLE_PROPS = [
  'display', 'position', 'top', 'left', 'right', 'bottom', 'box-sizing',
  'width', 'height', 'min-width', 'min-height', 'max-width', 'max-height',
  'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
  'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
  'border-top-width', 'border-top-style', 'border-top-color',
  'border-right-width', 'border-right-style', 'border-right-color',
  'border-bottom-width', 'border-bottom-style', 'border-bottom-color',
  'border-left-width', 'border-left-style', 'border-left-color',
  'border-collapse', 'border-spacing', 'table-layout', 'vertical-align',
  'flex', 'flex-direction', 'flex-wrap', 'align-items', 'align-self', 'justify-content', 'gap', 'row-gap', 'column-gap',
  'grid-template-columns', 'place-items',
  'overflow', 'overflow-wrap', 'word-break', 'white-space', 'text-overflow',
  'font-family', 'font-size', 'font-weight', 'font-style', 'line-height', 'letter-spacing', 'text-transform',
  'text-align', 'text-decoration-line', 'text-decoration-color', 'color', 'opacity',
  'background-color', 'box-shadow', 'list-style-type', 'list-style-position',
  '-webkit-line-clamp', '-webkit-box-orient',
] as const;

const fontCssCache = new Map<string, Promise<string>>();

async function toDataUrl(url: string): Promise<string> {
  const res = await fetch(url, { mode: 'cors' });
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

/** @font-face rules for every Google Fonts stylesheet on the page, with font files embedded. */
async function embeddedFontCss(): Promise<string> {
  // stylesheet links only: the preconnect hints point at the same host
  const links = [...document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href*="fonts.googleapis.com"]')];
  const parts = await Promise.all(
    links.map((link) => {
      let p = fontCssCache.get(link.href);
      if (!p) {
        p = (async () => {
          try {
            const css = await (await fetch(link.href, { mode: 'cors' })).text();
            const urls = [...new Set([...css.matchAll(/url\(([^)]+)\)/g)].map((m) => m[1]!.replace(/["']/g, '')))];
            let out = css;
            for (const u of urls) {
              try {
                out = out.split(u).join(await toDataUrl(u));
              } catch {
                /* embedding failed: handled below */
              }
            }
            // Any remote reference left would taint the export canvas: drop those sources entirely.
            out = out.replace(/src\s*:[^;]*url\((["']?)https?:[^)]*\1\)[^;]*;/gi, '');
            return out.replace(/url\((["']?)https?:[^)]*\1\)/gi, 'url(data:,)');
          } catch {
            return '';
          }
        })();
        fontCssCache.set(link.href, p);
      }
      return p;
    }),
  );
  return parts.join('\n');
}

function inlineStyles(src: Element, dst: Element): void {
  const cs = getComputedStyle(src);
  const el = dst as HTMLElement;
  let text = '';
  for (const prop of STYLE_PROPS) {
    const v = cs.getPropertyValue(prop);
    if (v && v !== 'normal' && v !== 'none' && v !== 'auto' && v !== '0px') text += `${prop}:${v};`;
    else if (v && (prop === 'display' || prop === 'position' || prop === 'white-space' || prop === 'overflow')) text += `${prop}:${v};`;
  }
  // background-image urls would not load (and could taint): keep gradients only
  const bg = cs.getPropertyValue('background-image');
  if (bg && bg !== 'none' && !/url\(/.test(bg)) text += `background-image:${bg};`;
  el.setAttribute('style', text);
  const s = src.children;
  const d = dst.children;
  for (let i = 0; i < s.length && i < d.length; i++) inlineStyles(s[i]!, d[i]!);
}

/** Copies checkbox state and drops elements that cannot render or should not appear. */
function cleanClone(src: Element, dst: Element): void {
  const srcInputs = src.querySelectorAll('input');
  const dstInputs = dst.querySelectorAll('input');
  srcInputs.forEach((inp, i) => {
    const d = dstInputs[i];
    if (!d) return;
    if (inp.type === 'checkbox') {
      if (inp.checked) d.setAttribute('checked', '');
      else d.removeAttribute('checked');
    }
  });
  dst.querySelectorAll('img, video, canvas, button, .kb-x, .placeholder').forEach((e) => e.remove());
}

/**
 * Rasterises `el` (an `.item` element) at `scale`. The element's own box is w×h CSS px.
 * Returns the decoded <img> rather than an ImageBitmap: Chrome taints a canvas when a
 * foreignObject SVG is drawn via createImageBitmap or a blob: URL, but not when the data: URL
 * image element itself is drawn.
 */
export async function elementToImage(el: Element, w: number, h: number, scale: number): Promise<HTMLImageElement> {
  const clone = el.cloneNode(true) as HTMLElement;
  inlineStyles(el, clone);
  cleanClone(el, clone);
  clone.style.transform = 'none';
  clone.style.position = 'static';
  clone.style.outline = 'none';
  const fonts = await embeddedFontCss();
  const xml = new XMLSerializer().serializeToString(clone);
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${Math.ceil(w * scale)}" height="${Math.ceil(h * scale)}" viewBox="0 0 ${w} ${h}">` +
    `<style>${fonts}</style>` +
    `<foreignObject width="${w}" height="${h}"><div xmlns="http://www.w3.org/1999/xhtml" style="width:${w}px;height:${h}px;">${xml}</div></foreignObject></svg>`;
  const url = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
  const img = new Image();
  img.decoding = 'async';
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('svg render failed'));
    img.src = url;
  });
  return img;
}
